import React from 'react';
import StatusIcon from 'src/components/StatusIcon';
import { GracePeriodValues } from './constants/distributors';
import { TransactionsCategory } from './constants/transactions';
import { getEquipmentDescription } from './equipments';
import momentLocalTimezone from './momentLocalTimezone';
import {
  ContractsComments,
  ContractsEventsStatus
} from './constants/contracts';
import { IconStatusAndColor } from './constants/common';
// eslint-disable-next-line import/prefer-default-export
export const getContractDescription = contract => {
  return `Folio ${contract.folio} -  Equipo ${getEquipmentDescription(
    contract.equipment
  )}`;
};

export const calculateFirstRentDateFromGracePeriod = (
  startDate,
  gracePeriod,
  isFirstUpfrontRentModality,
  initialPayment
) => {
  if (isFirstUpfrontRentModality) {
    switch (gracePeriod) {
      case GracePeriodValues.THIRTY:
        return startDate.clone().add(1, 'months');
      case GracePeriodValues.SIXTY:
        return startDate.clone().add(2, 'months');
      case GracePeriodValues.NINETY:
        return startDate.clone().add(3, 'months');
      default:
        return startDate.clone();
    }
  }

  if (initialPayment === 0) {
    return startDate.clone();
  }

  switch (gracePeriod) {
    case GracePeriodValues.SAME_DAY_AS_CONTRACT_STARTS:
      return startDate.clone();
    case GracePeriodValues.ZERO:
      return startDate.clone().add(1, 'months');
    case GracePeriodValues.THIRTY:
      return startDate.clone().add(2, 'months');
    case GracePeriodValues.SIXTY:
      return startDate.clone().add(3, 'months');
    case GracePeriodValues.NINETY:
      return startDate.clone().add(4, 'months');
    default:
      return startDate.clone();
  }
};

export const calculateSecondRentDateFromGracePeriod = (
  startDate,
  gracePeriod
) => {
  switch (gracePeriod) {
    case GracePeriodValues.SAME_DAY_AS_CONTRACT_STARTS:
      return startDate.clone();
    case GracePeriodValues.ZERO:
      return startDate.clone().add(1, 'months');
    case GracePeriodValues.THIRTY:
      return startDate.clone().add(2, 'months');
    case GracePeriodValues.SIXTY:
      return startDate.clone().add(3, 'months');
    case GracePeriodValues.NINETY:
      return startDate.clone().add(4, 'months');
    default:
      return startDate.clone();
  }
};

export const adjustTo25IfTodayIsAfter25 = inputDate => {
  const today = momentLocalTimezone();
  if (today.date() > 25) {
    return inputDate.clone().date(25);
  }
  return inputDate;
};

export const getFirstRentDate = (
  newDate,
  initialPayment,
  isFirstUpfrontRentModality,
  gracePeriod
) => {
  if (Number(initialPayment) === 0 && !isFirstUpfrontRentModality) {
    return newDate;
  }

  return calculateFirstRentDateFromGracePeriod(
    newDate,
    gracePeriod,
    isFirstUpfrontRentModality
  );
};

export const shouldDisableGracePeriod = (
  enableMsi,
  initialPayment,
  isFirstUpfrontRentModality
) => {
  return (
    enableMsi || (Number(initialPayment) === 0 && !isFirstUpfrontRentModality)
  );
};

export const calculateEndDateFromGracePeriod = (
  startDate,
  term,
  gracePeriod,
  isFirstUpfrontRentModality
) => {
  if (isFirstUpfrontRentModality) {
    switch (gracePeriod) {
      case GracePeriodValues.THIRTY:
        return startDate.clone().add(term, 'months');
      case GracePeriodValues.SIXTY:
        return startDate.clone().add(term + 1, 'months');
      case GracePeriodValues.NINETY:
        return startDate.clone().add(term + 2, 'months');
      default:
        return startDate.clone();
    }
  }

  switch (gracePeriod) {
    case GracePeriodValues.SAME_DAY_AS_CONTRACT_STARTS:
      return startDate.clone().add(term - 1, 'months');
    case GracePeriodValues.ZERO:
      return startDate.clone().add(term, 'months');
    case GracePeriodValues.THIRTY:
      return startDate.clone().add(term + 1, 'months');
    case GracePeriodValues.SIXTY:
      return startDate.clone().add(term + 2, 'months');
    case GracePeriodValues.NINETY:
      return startDate.clone().add(term + 3, 'months');
    default:
      return startDate.clone();
  }
};

export const selectAmountFromCategory = (category, contract) => {
  switch (category) {
    case TransactionsCategory.RENT:
      return contract.rent;
    case TransactionsCategory.UPFRONT_RENT:
      return contract.rent;
    case TransactionsCategory.INITIAL_PAYMENT:
      return contract.initialPayment;
    case TransactionsCategory.RESIDUAL:
      return contract.residualPayment;
    default:
      return 0;
  }
};

export const getUpfrontRentDisplayInfo = (contract, unreconciledAmount = 0, toleranceAmount = 0) => {
  const upfrontRentAmount = contract
    ? Number(selectAmountFromCategory(TransactionsCategory.UPFRONT_RENT, contract)) || 0
    : 0;
  const unreconciled = Number(unreconciledAmount);
  const tolerance = Number(toleranceAmount);
  return {
    upfrontRentAmount,
    rentExceedsUnreconciled: unreconciled + tolerance < upfrontRentAmount,
    toleranceApplied:
      upfrontRentAmount > unreconciled && upfrontRentAmount <= unreconciled + tolerance
  };
};

export const canSubmitUpfrontRent = (
  hasContract,
  rentExceedsUnreconciled,
  isLoadingSubmit
) =>
  Boolean(
    hasContract && !rentExceedsUnreconciled && !isLoadingSubmit
  );

export function isPayoffExceedsUnreconciled(
  values,
  unreconciledAmount,
  isLoadingPayoffAmount,
  toleranceAmount = 0
) {
  if (!values.isPayoffRequest || isLoadingPayoffAmount) return false;
  return Number(values.amount) > unreconciledAmount + Number(toleranceAmount);
}

export function isInvalidPayoffAmount(values, isLoadingPayoffAmount) {
  if (!values.isPayoffRequest || isLoadingPayoffAmount) return false;
  return Number(values.amount) <= 0;
}

export function isSubmitDisabled(
  values,
  unreconciledAmount,
  isLoadingPayoffAmount,
  isLoadingSubmit,
  toleranceAmount = 0
) {
  if (isLoadingSubmit || !values.contract) return true;
  if (values.isPayoffRequest) {
    return (
      isLoadingPayoffAmount ||
      isInvalidPayoffAmount(values, isLoadingPayoffAmount) ||
      isPayoffExceedsUnreconciled(
        values,
        unreconciledAmount,
        isLoadingPayoffAmount,
        toleranceAmount
      )
    );
  }
  return false;
}

export const setGracePeriod = (values, parameter) => {
  if (values.isFirstUpfrontRentModality) {
    return GracePeriodValues.THIRTY;
  }
  if (values && values.initialPayment === 0) {
    return GracePeriodValues.SAME_DAY_AS_CONTRACT_STARTS;
  }
  return Number(parameter.defaultGracePeriod);
};

export const setInitialPayment = values => {
  if (values.isFirstUpfrontRentModality) {
    return 0;
  }

  return values.initialPayment ?? 0;
};

export const getFormattedContractComment = (comment, t) => {
  if (comment === ContractsComments.CEDED) {
    return t('COMMON.CEDED_CONTRACT');
  }
  if (comment === ContractsComments.EARLY_TERMINATED) {
    return t('COMMON.EARLY_TERMINATED_CONTRACT');
  }
  return t('COMMON.EMPTY');
};

export const getFormattedContractEventStatus = (status, t) => {
  if (status === ContractsEventsStatus.COMPLETED) {
    return t('COMMON.COMPLETED_EVENT');
  }
  if (status === ContractsEventsStatus.PENDING) {
    return t('COMMON.PENDING_EVENT');
  }
  return t('COMMON.EMPTY');
};

export const setGracePeriodForFirstUpfrontRentModalityMonthsDiff = months => {
  if (months === 3) {
    return GracePeriodValues.SIXTY;
  }
  if (months === 4) {
    return GracePeriodValues.NINETY;
  }
  return GracePeriodValues.THIRTY;
};

export const getEventContractStatusIcon = status => {
  switch (status) {
    case ContractsEventsStatus.COMPLETED:
      return (
        <StatusIcon
          description="COMMON.COMPLETED_EVENT"
          type={IconStatusAndColor.OK.status}
        />
      );
    case ContractsEventsStatus.PENDING:
      return (
        <StatusIcon
          description="COMMON.PENDING_EVENT"
          type={IconStatusAndColor.WARNING.status}
        />
      );
    case ContractsEventsStatus.CANCELLED:
      return (
        <StatusIcon
          description="COMMON.CANCELLED"
          type={IconStatusAndColor.ERROR.status}
        />
      );

    default:
      return null;
  }
};
